import { ClipboardText } from '@phosphor-icons/react';
import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { users } from '../../api/endpoints';
import { EventCard } from '../../components/EventCard';
import { ScreenError, ScreenLoader, SubScreen } from '../../components/layout/Screens';
import { EmptyState, Tabs } from '../../components/ui/Bits';
import { useAsync } from '../../hooks/useAsync';
import { useSwipeTabs } from '../../hooks/useSwipeTabs';
import { useTabMemory } from '../../hooks/useTabMemory';
import { shortDate } from '../../utils/format';
import { t } from '../../i18n';

type Tab = 'joined' | 'organized' | 'past';

/** Les trois onglets, hors du rendu : le geste de balayage les lit aussi. */
const ONGLETS: { value: Tab; label: string }[] = [
  { value: 'joined', label: t.sorties.miennes.onglets.inscrit },
  { value: 'organized', label: t.sorties.miennes.onglets.organise },
  { value: 'past', label: t.sorties.miennes.onglets.passes },
];

/** Chaque onglet se vide pour une raison differente, la phrase le dit. */
const EMPTY_HINTS: Record<Tab, string> = {
  joined: t.sorties.miennes.vides.inscrit,
  organized: t.sorties.miennes.vides.organise,
  past: t.sorties.miennes.vides.passes,
};

/**
 * "Mes evenements", ouvert depuis le profil.
 *
 * Trois onglets : ce a quoi je suis inscrit, ce que j'organise, ce qui est
 * passe. On ne masque rien ici — c'est une archive de ce qu'on a fait, et
 * en retirer des lignes la rendrait fausse. Masquer un evenement reste
 * possible depuis sa fiche, dans l'onglet Evenements.
 */
export function MyEventsScreen() {
  const [tab, setTab] = useTabMemory<Tab>('mes-evenements', 'joined', ONGLETS);
  /* Le balayage double le clic sur le titre : c'est le geste qu'on essaie
     devant deux onglets cote a cote. */
  const zoneRef = useRef<HTMLDivElement>(null);
  useSwipeTabs(zoneRef, tab, ONGLETS, setTab);
  const { data, loading, error, reload } = useAsync(() => users.myEvents(), [], 'my-events');

  const list =
    tab === 'joined'
      ? (data?.joined ?? [])
      : tab === 'organized'
        ? (data?.organized ?? [])
        : (data?.past ?? []);
  const emptyHint = EMPTY_HINTS[tab];
  /* Les feuilles en attente n'appartiennent qu'a l'organisateur : elles
     ouvrent l'onglet « Organisés », avant ce qui est a venir. */
  const feuilles = tab === 'organized' ? (data?.sheetsToClose ?? []) : [];

  return (
    <SubScreen title={t.sorties.miennes.titre}>
      <div className="ev-mine">
        <div ref={zoneRef}>
          <Tabs<Tab> value={tab} onChange={setTab} options={ONGLETS} />
        </div>

        {loading && <ScreenLoader />}
        {error && <ScreenError message={error} onRetry={reload} />}

        {/*
          Le rappel de feuille de presence.

          Il ouvre l'onglet parce que c'est la seule chose qui attend un
          geste : le reste de la liste se consulte, celle-ci se traite.
          L'organisateur n'a rien fait de mal — il a quelque chose a
          finir, et le ton reste celui d'un rappel, pas d'une alarme.
        */}
        {feuilles.length > 0 && (
          <div className="ev-mine__rappels">
            <p className="ev-mine__rappel-titre">
              <ClipboardText size={16} weight="fill" />
              {feuilles.length === 1
                ? t.sorties.miennes.uneFeuille
                : t.sorties.miennes.feuilles(feuilles.length)}
            </p>
            {feuilles.map((event) => (
              <Link
                key={event.id}
                to={`/app/events/${event.id}/manage`}
                className="card ev-mine__rappel"
              >
                <span className="ev-mine__rappel-nom">{event.title}</span>
                <span className="ev-mine__rappel-date">{shortDate(event.date)}</span>
              </Link>
            ))}
          </div>
        )}

        {data && list.length === 0 && feuilles.length === 0 && (
          <EmptyState title={t.sorties.miennes.rien} hint={emptyHint} />
        )}

        {data && list.length > 0 && (
          <div className="ev-mine__list">
            {list.map((event) => (
              <EventCard key={event.id} event={event} />
            ))}
          </div>
        )}
      </div>
    </SubScreen>
  );
}
